import React, { useEffect, useState } from 'react';
import { UserService } from '../../services/user.service';
import { InstitutionService } from '../../services/institution.service';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LANDING_ROUTE } from '../../utils/roleLanding';
import { useToast } from '../../context/ToastContext';
import { Modal, Button, Box, Select, MenuItem, FormControl, InputLabel, Tooltip, IconButton, Stack, Pagination, Alert, CircularProgress, TextField, Autocomplete, createFilterOptions } from '@mui/material';
import { ManageAccounts, Delete, LockReset, ToggleOn, ToggleOff, SupervisorAccount } from '@mui/icons-material';
import { UserType } from '../../interfaces/userType.enum';
import { ImpersonationGrantService } from '../../services/impersonation-grant.service';
import { IAvailableGrant } from '../../interfaces/impersonation-grant.interface';
import { IInstitution } from '../../interfaces/institution.interface';
import { displayUsername } from '../../utils/userUtils';
import { useTranslation } from 'react-i18next';
import { useRoleLabel } from './useRoleLabel';
import { useFreeTierNudge } from '../shared/FreeTierNudge';

interface Institution {
    id: string;
    name: string;
}

interface User {
    id: string;
    username: string;
    phone: string;
    type: UserType;
    isActive: boolean;
    institutionName?: string; // Added to show which institution the user belongs to
}

const institutionFilterOptions = createFilterOptions<IInstitution>({ limit: 10 });

const AllInstitutionUsers = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedType, setSelectedType] = useState<UserType | ''>('');
    const [userInstitutions, setUserInstitutions] = useState<Institution[]>([]);
    const [selectedInstitutionId, setSelectedInstitutionId] = useState<string>('');
    const [loadingInstitutions, setLoadingInstitutions] = useState(false);
    const [availableGrants, setAvailableGrants] = useState<IAvailableGrant[]>([]);
    const [institutions, setInstitutions] = useState<IInstitution[]>([]);
    const [filterInstitutionId, setFilterInstitutionId] = useState<string>('');
    const navigate = useNavigate();
    const { isAuthenticated, impersonate, user: authUser } = useAuth();
    const isSystemAdmin = authUser?.roles === 'SystemAdmin';
    const toast = useToast();
    const { t } = useTranslation(['users', 'common']);
    const freeTier = useFreeTierNudge();
    const roleLabel = useRoleLabel();
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });
    const [page,] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchAvailableGrants();
        fetchInstitutions();
    }, [isAuthenticated, navigate, page]);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(handle);
    }, [searchTerm]);

    useEffect(() => {
        if (isAuthenticated) {
            fetchAllUsers(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, filterInstitutionId, isAuthenticated]);

    const fetchAvailableGrants = async () => {
        try {
            const grants = await ImpersonationGrantService.getAvailableGrants();
            setAvailableGrants(grants);
        } catch {
            // Silently fail — not critical
        }
    };

    const handleImpersonate = async (targetUserId: string) => {
        try {
            await impersonate(targetUserId);
            navigate(LANDING_ROUTE);
        } catch (err: any) {
            toast.showApiError(err, t('allInstitutionUsers.impersonateFailed'));
        }
    };

    const fetchInstitutions = async () => {
        try {
            const response = await InstitutionService.getInstitutions(0, 100);
            setInstitutions(response.content || []);
        } catch (err: any) {
            console.error('Error fetching institutions:', err);
        }
    };

    const fetchAllUsers = async (page: number = 0, institutionId?: string) => {
        setLoading(true);
        try {
            const selectedInstitution = institutionId !== undefined ? institutionId : filterInstitutionId;
            const response = selectedInstitution
                ? await UserService.getInstitutionUsers(page, 10, selectedInstitution, debouncedSearch)
                : await UserService.getUsers(page, 10, debouncedSearch);
            setUsers(response.content || []);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err: any) {
            toast.showApiError(err, t('manage.toasts.loadUsersFailed'));
            console.error('Error fetching users:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleActive = async (user: User) => {
        try {
            await UserService.toggleUserActive(user.id);
            toast.showSuccess(user.isActive !== false ? t('manage.toasts.userDeactivated') : t('manage.toasts.userActivated'));
            await fetchAllUsers(pageInfo.currentPage);
        } catch (error: any) {
            if (!freeTier.handle(error)) toast.showApiError(error, t('manage.toasts.updateStatusFailed'));
        }
    };

    const handleOpenTypeModal = (user: User) => {
        setSelectedUser(user);
        setSelectedType(user.type);
        setIsTypeModalOpen(true);
    };

    const handleCloseTypeModal = () => {
        setIsTypeModalOpen(false);
        setSelectedUser(null);
        setSelectedType('');
    };

    const handleOpenDeleteModal = (user: User) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedUser(null);
    };

    const handleOpenResetPasswordModal = async (user: User) => {
        setSelectedUser(user);
        setIsResetPasswordModalOpen(true);
        setLoadingInstitutions(true);
        setSelectedInstitutionId('');
        try {
            const institutions = await InstitutionService.getInstitutionsByUserId(user.id);
            setUserInstitutions(institutions);
        } catch (error) {
            console.error('Error fetching user institutions:', error);
            setUserInstitutions([]);
        } finally {
            setLoadingInstitutions(false);
        }
    };

    const handleCloseResetPasswordModal = () => {
        setIsResetPasswordModalOpen(false);
        setSelectedUser(null);
        setUserInstitutions([]);
        setSelectedInstitutionId('');
    };

    const handleTypeChange = async () => {
        if (!selectedUser || !selectedType) return;

        try {
            await UserService.updateUserType(selectedUser.id, selectedType);
            toast.showSuccess(t('manage.toasts.typeUpdated'));
            await fetchAllUsers();
            handleCloseTypeModal();
        } catch (error: any) {
            console.error('Error updating user type:', error);
            if (!freeTier.handle(error)) toast.showApiError(error, t('manage.toasts.typeUpdateFailed'));
            handleCloseTypeModal();
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) return;

        try {
            await UserService.deleteUser(selectedUser.id);
            toast.showSuccess(t('manage.toasts.userDeleted'));
            await fetchAllUsers();
            handleCloseDeleteModal();
        } catch (error: any) {
            console.error('Error deleting user:', error);
            toast.showApiError(error, t('manage.toasts.deleteFailed'));
            handleCloseDeleteModal();
        }
    };

    const handleResetPassword = async () => {
        if (!selectedUser || !selectedInstitutionId) return;

        try {
            await UserService.resetUserPassword(selectedUser.id, selectedInstitutionId);
            toast.showSuccess(t('manage.toasts.passwordReset'));
            handleCloseResetPasswordModal();
        } catch (error) {
            console.error('Error resetting password:', error);
            toast.showApiError(error, t('manage.toasts.resetPasswordFailed'));
        }
    };

    const handleInstitutionFilterChange = (institutionId: string) => {
        setFilterInstitutionId(institutionId);
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchAllUsers(page - 1); // API uses 0-based indexing
    };

    if (loading) return <div data-testid="loading-state">{t('manage.loading')}</div>;

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>{t('allInstitutionUsers.title')}</h2>
                <Stack direction="row" spacing={2}>
                    <TextField
                        label={t('manage.searchByUsername')}
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ width: 260 }}
                    />
                    <Autocomplete
                        sx={{ minWidth: 250 }}
                        size="small"
                        options={institutions}
                        filterOptions={institutionFilterOptions}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={institutions.find((institution) => institution.id === filterInstitutionId) ?? null}
                        onChange={(_event, newValue) => handleInstitutionFilterChange(newValue?.id ?? '')}
                        renderInput={(params) => (
                            <TextField {...params} label={t('manage.filterByInstitution')} placeholder={t('allInstitutionUsers.allInstitutions')} />
                        )}
                    />
                </Stack>
            </div>

            <div style={{ marginBottom: '20px' }}>
                {t('manage.totalUsers', { total: pageInfo.totalElements })}
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        <th style={tableHeaderStyle}>{t('manage.columns.username')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.phone')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.type')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.status')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.institution')}</th>
                        <th style={tableHeaderStyle}>{t('manage.columns.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <tr key={user.username}>
                            <td style={tableCellStyle}>{displayUsername(user.username)}</td>
                            <td style={tableCellStyle}>{user.phone}</td>
                            <td style={tableCellStyle}>{roleLabel(user.type)}</td>
                            <td style={tableCellStyle}>
                                <span style={{ color: user.isActive !== false ? 'green' : 'red' }}>
                                    {user.isActive !== false ? t('common:status.active') : t('common:status.inactive')}
                                </span>
                            </td>
                            <td style={tableCellStyle}>{user.institutionName || '-'}</td>
                            <td style={tableCellStyle}>
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={user.isActive !== false ? t('manage.deactivateUser') : t('manage.activateUser')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggleActive(user)}
                                            color={user.isActive !== false ? "success" : "default"}
                                            aria-label={user.isActive !== false ? t('manage.deactivateUser') : t('manage.activateUser')}
                                        >
                                            {user.isActive !== false ? <ToggleOn /> : <ToggleOff />}
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('manage.changeType')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenTypeModal(user)}
                                            color="primary"
                                            aria-label={t('manage.changeType')}
                                        >
                                            <ManageAccounts />
                                        </IconButton>
                                    </Tooltip>
                                    {isSystemAdmin && (
                                        <Tooltip title={t('manage.deleteUser')} arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenDeleteModal(user)}
                                                color="error"
                                                aria-label={t('manage.deleteUser')}
                                            >
                                                <Delete />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title={t('manage.resetPassword')} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenResetPasswordModal(user)}
                                            color="secondary"
                                            aria-label={t('manage.resetPassword')}
                                        >
                                            <LockReset />
                                        </IconButton>
                                    </Tooltip>
                                    {availableGrants.some(g => g.userId === user.id) && (
                                        <Tooltip title={t('allInstitutionUsers.impersonateUser')} arrow>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleImpersonate(user.id)}
                                                color="warning"
                                                aria-label={t('allInstitutionUsers.impersonateUser')}
                                            >
                                                <SupervisorAccount />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Type Change Modal */}
            <Modal
                open={isTypeModalOpen}
                onClose={handleCloseTypeModal}
                aria-describedby="change-type-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>{t('manage.changeUserType')}</h3>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>{t('manage.userType')}</InputLabel>
                        <Select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value as UserType)}
                            label={t('manage.userType')}
                        >
                            {Object.values(UserType).map((type) => (
                                <MenuItem key={type} value={type}>
                                    {roleLabel(type)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseTypeModal} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        <Button onClick={handleTypeChange} variant="contained" color="primary">
                            {t('manage.saveChanges')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                aria-describedby="delete-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>{t('manage.deleteUser')}</h3>
                    <p>{t('manage.deleteConfirm', { username: displayUsername(selectedUser?.username) })}</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseDeleteModal} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        <Button onClick={handleDeleteUser} variant="contained" color="error">
                            {t('common:actions.delete')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Reset Password Modal */}
            <Modal
                open={isResetPasswordModalOpen}
                onClose={handleCloseResetPasswordModal}
                aria-describedby="reset-password-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }}>
                    <h3>{t('manage.resetPassword')}</h3>
                    <p>{t('manage.resetPasswordConfirm', { username: displayUsername(selectedUser?.username) })}</p>

                    {loadingInstitutions ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : userInstitutions.length > 0 ? (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>{t('allInstitutionUsers.selectInstitution')}</InputLabel>
                            <Select
                                value={selectedInstitutionId}
                                onChange={(e) => setSelectedInstitutionId(e.target.value)}
                                label={t('allInstitutionUsers.selectInstitution')}
                            >
                                {userInstitutions.map((institution) => (
                                    <MenuItem key={institution.id} value={institution.id}>
                                        {institution.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {t('allInstitutionUsers.noInstitution')}
                        </Alert>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseResetPasswordModal} variant="outlined">
                            {t('common:actions.cancel')}
                        </Button>
                        <Button
                            onClick={handleResetPassword}
                            variant="contained"
                            color="primary"
                            disabled={!selectedInstitutionId || loadingInstitutions}
                        >
                            {t('manage.resetPassword')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {freeTier.element}
        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default AllInstitutionUsers;
